import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Share2, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Brand palette — must stay in sync with tailwind theme
const BRAND = {
  bgTop: "#2E4232",       // deep sage
  bgBottom: "#243329",    // darker sage for gradient
  cream: "#F5EFE4",
  accent: "#C46650",      // terracotta
  accentSoft: "rgba(196, 102, 80, 0.15)",
  serif: "Georgia, 'Playfair Display', serif",
  sans: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
};

const gradeLetter = (pct) => {
  if (pct >= 90) return "A";
  if (pct >= 80) return "B";
  if (pct >= 70) return "C";
  if (pct >= 60) return "D";
  return "F";
};

const drawStoryCard = (canvas, { pct, book, chapter, edition, name, referralLink }) => {
  const W = 1080, H = 1920;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, BRAND.bgTop);
  grad.addColorStop(1, BRAND.bgBottom);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Subtle grain (very light noise dots)
  ctx.save();
  ctx.globalAlpha = 0.05;
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = i % 2 ? BRAND.cream : BRAND.accent;
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }
  ctx.restore();

  // Top brand row
  ctx.fillStyle = BRAND.cream;
  ctx.font = `bold 44px ${BRAND.sans}`;
  ctx.textAlign = "left";
  ctx.fillText("📖 Lectura", 90, 150);
  ctx.font = `500 32px ${BRAND.sans}`;
  ctx.fillStyle = "rgba(245, 239, 228, 0.65)";
  ctx.fillText("Study smarter. Score higher.", 90, 200);

  // "I just scored" label
  ctx.textAlign = "center";
  ctx.fillStyle = BRAND.accent;
  ctx.font = `600 44px ${BRAND.sans}`;
  ctx.letterSpacing = "0.2em";
  ctx.fillText("I JUST SCORED", W / 2, 520);

  // Big percentage — hero
  ctx.fillStyle = BRAND.cream;
  ctx.font = `bold 380px ${BRAND.serif}`;
  ctx.fillText(`${pct}%`, W / 2, 900);

  // Grade letter pill (below the percentage)
  const letter = gradeLetter(pct);
  ctx.fillStyle = BRAND.accent;
  const pillW = 220, pillH = 90;
  ctx.beginPath();
  const pillX = W / 2 - pillW / 2;
  const pillY = 940;
  ctx.roundRect(pillX, pillY, pillW, pillH, 45);
  ctx.fill();
  ctx.fillStyle = BRAND.cream;
  ctx.font = `bold 52px ${BRAND.sans}`;
  ctx.textBaseline = "middle";
  ctx.fillText(`Grade ${letter}`, W / 2, pillY + pillH / 2 + 3);
  ctx.textBaseline = "alphabetic";

  // "on the quiz for" label
  ctx.fillStyle = "rgba(245, 239, 228, 0.7)";
  ctx.font = `500 36px ${BRAND.sans}`;
  ctx.fillText("on the quiz for", W / 2, 1140);

  // Book title — wrapping (up to 2 lines)
  ctx.fillStyle = BRAND.cream;
  ctx.font = `bold 72px ${BRAND.serif}`;
  const wrapLines = wrapText(ctx, book, W - 200, 2);
  const bookY = 1240;
  wrapLines.forEach((line, i) => {
    ctx.fillText(line, W / 2, bookY + i * 90);
  });

  // Chapter / edition subline
  const meta = [chapter && `Chapter ${chapter.replace(/^chapter\s*/i, "")}`, edition]
    .filter(Boolean).join(" · ");
  if (meta) {
    ctx.fillStyle = "rgba(245, 239, 228, 0.75)";
    ctx.font = `italic 40px ${BRAND.serif}`;
    ctx.fillText(meta, W / 2, bookY + wrapLines.length * 90 + 60);
  }

  // Divider
  ctx.strokeStyle = "rgba(245, 239, 228, 0.25)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(200, 1620);
  ctx.lineTo(W - 200, 1620);
  ctx.stroke();

  // Referral CTA
  ctx.fillStyle = BRAND.cream;
  ctx.font = `600 36px ${BRAND.sans}`;
  ctx.fillText(`Study with me — get 7 free lectures 🎁`, W / 2, 1700);

  // Referral URL — auto-shrink to fit width (max 40px, min 22px)
  ctx.fillStyle = BRAND.accent;
  let urlSize = 40;
  const urlMaxW = W - 160;
  while (urlSize > 22) {
    ctx.font = `bold ${urlSize}px ${BRAND.sans}`;
    if (ctx.measureText(referralLink).width <= urlMaxW) break;
    urlSize -= 2;
  }
  ctx.fillText(referralLink, W / 2, 1770);

  // Bottom name credit
  if (name) {
    ctx.fillStyle = "rgba(245, 239, 228, 0.55)";
    ctx.font = `500 30px ${BRAND.sans}`;
    ctx.fillText(`— ${name}`, W / 2, 1860);
  }
};

// Naive word-wrap up to `maxLines`. Truncates with "…" when overflowing.
const wrapText = (ctx, text, maxWidth, maxLines) => {
  if (!text) return [""];
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    lines.length = maxLines;
    let last = lines[maxLines - 1];
    while (ctx.measureText(last + "…").width > maxWidth && last.length > 3) last = last.slice(0, -1);
    lines[maxLines - 1] = last + "…";
  }
  return lines;
};

export const ShareGradeCard = ({ open, onClose, pct, lecture }) => {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const referralLink = user?.referral_code
    ? `${window.location.origin}/join/${user.referral_code}`
    : `${window.location.origin}`;

  useEffect(() => {
    if (!open) return;
    setReady(false);
    // Defer to next frame so the canvas is mounted
    requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Ensure roundRect exists (older Safari fallback)
      if (!canvas.getContext("2d").roundRect) {
        CanvasRenderingContext2D.prototype.roundRect = function (x, y, w, h, r) {
          this.beginPath();
          this.moveTo(x + r, y);
          this.arcTo(x + w, y, x + w, y + h, r);
          this.arcTo(x + w, y + h, x, y + h, r);
          this.arcTo(x, y + h, x, y, r);
          this.arcTo(x, y, x + w, y, r);
          this.closePath();
          return this;
        };
      }
      drawStoryCard(canvas, {
        pct,
        book: lecture?.book_title || lecture?.title || "Study Session",
        chapter: lecture?.chapters || "",
        edition: lecture?.edition || "",
        name: user?.name || "",
        referralLink,
      });
      setReady(true);
    });
  }, [open, pct, lecture, user, referralLink]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lectura-score-${pct}pct.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Saved — drop it in your Instagram Story!");
    }, "image/png", 0.95);
  };

  const share = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSharing(true);
    try {
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
      const file = new File([blob], `lectura-score-${pct}pct.png`, { type: "image/png" });
      const shareText = `I scored ${pct}% on ${lecture?.book_title || "my quiz"} with @lectura 🎓 Study with me — ${referralLink}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText, title: "My Lectura score" });
        toast.success("Shared!");
      } else {
        // Fallback: download
        download();
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share — try Save & post manually");
    } finally {
      setSharing(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <DialogTitle className="sr-only">Share your grade</DialogTitle>
        <DialogDescription className="sr-only">Preview and share a branded image of your quiz score.</DialogDescription>

        <div className="p-6 pb-4 text-center">
          <h2 className="font-serif text-2xl font-bold">Share your grade 📸</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Auto-branded for Instagram Story · your invite link is baked in
          </p>
        </div>

        <div className="px-6">
          <div className="relative rounded-2xl overflow-hidden border border-border bg-secondary/30 aspect-[9/16]">
            {!ready && (
              <div className="absolute inset-0 grid place-items-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
              </div>
            )}
            <canvas
              ref={canvasRef}
              data-testid="share-grade-canvas"
              className="w-full h-full block"
              style={{ display: ready ? "block" : "none" }}
            />
          </div>
        </div>

        <div className="p-6 pt-4 space-y-2">
          <button
            data-testid="share-grade-share-btn"
            onClick={share}
            disabled={!ready || sharing}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition"
          >
            {sharing ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening share…</> : <><Share2 className="h-4 w-4" /> Share to Instagram / anywhere</>}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="share-grade-download-btn"
              onClick={download}
              disabled={!ready}
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-border py-3 text-sm font-semibold hover:bg-secondary/60 transition disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> Save image
            </button>
            <button
              data-testid="share-grade-copy-link-btn"
              onClick={copyLink}
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-border py-3 text-sm font-semibold hover:bg-secondary/60 transition"
            >
              {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy invite link</>}
            </button>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-2">
            Tip: on iPhone, tap Share → "Instagram Stories" for one-tap posting.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
